import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { FaStar, FaGift } from 'react-icons/fa';
import useDailyRewardStore from '../stores/dailyRewardStore';
import { toast } from 'react-toastify';
import { pluralizeDays } from '../utils/pluralize';
import useUserStore from '../stores/userStore';
import CurrencyIcon from './CurrencyIcon';

function getNextRewardTime(streak) {
	if (!streak || !streak.streakDays) return null;
	const lastClaimed = streak.streakDays.filter(d => d.claimed).pop();
	if (!lastClaimed) return null;
	const lastDate = new Date(lastClaimed.date);
	lastDate.setDate(lastDate.getDate() + 1);
	return lastDate;
}

function getNextClaimTime(lastClaim) {
	if (!lastClaim) return null;
	const lastClaimDate = new Date(lastClaim);
	// Следующий клейм через 24 часа
	return new Date(lastClaimDate.getTime() + 24 * 60 * 60 * 1000);
}

function getNextDailyClaimUTC(lastClaim) {
	if (!lastClaim) return null;
	// Текущее время в UTC
	const now = new Date();
	const utcNow = new Date(now.toISOString().slice(0, 19) + 'Z');

	// Следующее 00:00 UTC после lastClaim
	const lastClaimDate = new Date(lastClaim);
	let nextClaimUTC = new Date(lastClaimDate);
	nextClaimUTC.setUTCHours(0, 0, 0, 0);
	nextClaimUTC.setUTCDate(nextClaimUTC.getUTCDate() + 1);

	// Если уже наступило следующее 00:00 UTC, но пользователь не клеймил — можно клеймить
	if (utcNow >= nextClaimUTC) {
		return 0; // Можно клеймить!
	}

	// Сколько осталось до следующего 00:00 UTC
	return nextClaimUTC - utcNow;
}

function formatTime(ms) {
	if (ms <= 0) return 'Доступно!';
	const h = Math.floor(ms / 1000 / 60 / 60);
	const m = Math.floor((ms / 1000 / 60) % 60);
	const s = Math.floor((ms / 1000) % 60);
	return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

const DailyRewardModal = ({ isOpen, onClose }) => {
	const { t } = useTranslation();
	const { streak, loading, fetchStreak, claimStreakBonus, claimDaily, setStreak } = useDailyRewardStore();
	const [timer, setTimer] = useState('');
	const fetchUser = useUserStore(state => state.fetchUser);

	useEffect(() => {
		if (!streak) return;
		const lastClaim = streak.lastClaim;
		if (streak.nextRewardAvailable) {
			setTimer('');
			return;
		}
		if (!lastClaim) {
			setTimer('--:--:--');
			return;
		}
		const interval = setInterval(() => {
			const msLeft = getNextDailyClaimUTC(lastClaim);
			setTimer(formatTime(msLeft));
		}, 1000);
		return () => clearInterval(interval);
	}, [streak]);

	if (!isOpen) return null;

	return (
		<ModalOverlay onClick={onClose}>
			<ModalContent onClick={e => e.stopPropagation()}>
				<ModalHeader>
					<Title>{t('tasks.daily_rewards')}</Title>
					<CloseButton onClick={onClose}>
						<IoClose size={24} />
					</CloseButton>
				</ModalHeader>

				{!streak ? (
					<div style={{ textAlign: 'center', padding: 32 }}>{t('common.loading')}</div>
				) : (
					<RewardsContainer>
						<ProgressLine>
							{streak.streakDays.map((day, idx) => (
								<ProgressSegment key={day.date}>
									<StarIcon claimed={day.claimed}>
										<FaStar size={24} />
									</StarIcon>
									{idx < streak.streakDays.length - 1 && (
										<LineSegment active={streak.streakDays[idx + 1]?.claimed} />
									)}
								</ProgressSegment>
							))}
							<ChestIconBlock>
								<ChestIcon active={streak.canClaimBonus && !streak.bonusAlreadyClaimed}>
									<FaGift size={32} />
								</ChestIcon>
								<ChestBonusText>
									<CurrencyIcon size={16} />{streak.streakBonusTokens}
								</ChestBonusText>
							</ChestIconBlock>
						</ProgressLine>

						{streak.currentStreak === 0 && !streak.streakDays.some(d => d.claimed) && (
							<StreakText style={{ textAlign: 'center', marginTop: 8, color: '#FFD700' }}>
								{t('tasks.claim_first_reward')}
							</StreakText>
						)}

						<RewardInfo>
							<RewardText>
								{t('tasks.daily_reward_amount')}
								<RewardInline>
									<CurrencyIcon size={16} />
									100
								</RewardInline>
							</RewardText>
							{streak.currentStreak === 7 && streak.hasStreak && (
								<BonusRewardText>
									{t('tasks.chest_bonus')}
								</BonusRewardText>
							)}
						</RewardInfo>

						{streak.canClaimBonus && !streak.bonusAlreadyClaimed ? (
							<StreakInfo as="button"
								style={{
									cursor: 'pointer',
									background: '#2563eb',
									color: 'white',
									fontWeight: 600,
									fontSize: 16,
									border: 'none',
									margin: '0 auto 16px auto',
									transition: 'opacity 0.2s',
									opacity: loading ? 0.7 : 1
								}}
								onClick={async () => {
									const res = await claimStreakBonus();
									if (!res.success && res.error) {
										toast.error(res.error);
									} else {
										await fetchStreak();
										await fetchUser();
									}
								}}
								disabled={loading}
							>
								{loading ? t('common.loading') : t('tasks.claim_streak_bonus', { days: streak.currentStreak })}
							</StreakInfo>
						) : (
							<StreakInfo>
								<StreakText>
									{t('tasks.current_streak', { days: streak.currentStreak, days_word: pluralizeDays(streak.currentStreak) })}
								</StreakText>
							</StreakInfo>
						)}

						{streak.nextRewardAvailable ? (
							<PulseButton disabled={loading} onClick={async () => {
								const res = await claimDaily();
								if (!res.success && res.error) {
									toast.error(res.error);
								} else {
									await fetchUser();
									await fetchStreak();
								}
							}}>
								{loading ? t('common.loading') : t('tasks.daily_claim_action')}
							</PulseButton>
						) : (
							<TimerBlock>{t('tasks.next_reward')} {timer}</TimerBlock>
						)}
					</RewardsContainer>
				)}
			</ModalContent>
		</ModalOverlay>
	);
};

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: flex-end;
	z-index: 1000;
	transition: opacity 0.3s;
`;

const ModalContent = styled.div`
	background: ${props => props.theme.card.background};
	border-radius: 24px 24px 0 0;
	padding: 24px 24px 100px 24px;
	width: 100%;
	max-width: 500px;
	box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
	animation: modalIn 0.35s cubic-bezier(0.22, 1, 0.36, 1);
	position: relative;
	z-index: 10000;
	@keyframes modalIn {
		0% {
			opacity: 0;
			transform: scale(0.95) translateY(40px);
		}
		100% {
			opacity: 1;
			transform: scale(1) translateY(0);
		}
	}
`;

const ModalHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
`;

const Title = styled.h2`
	font-size: 20px;
	font-weight: 600;
	margin: 0;
	color: ${props => props.theme.text.primary};
`;

const CloseButton = styled.button`
	background: none;
	border: none;
	color: ${props => props.theme.text.secondary};
	cursor: pointer;
	padding: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	transition: background 0.2s;

	&:hover {
		background: ${props => props.theme.background};
	}
`;

const RewardsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
	margin-bottom: 24px;
`;

const ProgressLine = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 16px;
	position: relative;
`;

const ProgressSegment = styled.div`
	display: flex;
	align-items: center;
	flex: 1;
`;

const StarIcon = styled.div`
	color: ${props => props.claimed ? '#FFD700' : props.theme.text.secondary};
	transition: color 0.3s;
`;

const LineSegment = styled.div`
	flex: 1;
	height: 2px;
	background: ${props => props.active ? props.theme.primary : props.theme.background};
	margin: 0 8px;
`;

const ChestIconBlock = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const ChestIcon = styled.div`
	color: ${props => props.active ? '#FFD700' : props.theme.text.secondary};
	margin-left: 8px;
	transition: color 0.3s;
`;

const ChestBonusText = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 14px;
	color: #FFD700;
	margin-top: 2px;
	font-weight: 600;
`;

const RewardInfo = styled.div`
	text-align: center;
`;

const RewardText = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	font-size: 16px;
	font-weight: 500;
	color: ${props => props.theme.text.primary};
	margin: 0 auto;
`;

const RewardInline = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
`;

const BonusRewardText = styled.div`
	font-size: 14px;
	color: #FFD700;
	margin-top: 4px;
`;

const StreakInfo = styled.div`
	text-align: center;
	padding: 16px;
	background: ${props => props.theme.background};
	border-radius: 12px;
`;

const StreakText = styled.div`
	font-size: 14px;
	color: ${props => props.theme.text.secondary};
`;

const DaysList = styled.div`
	display: flex;
	gap: 8px;
	justify-content: center;
`;

const DayItem = styled.div`
	background: ${props => props.claimed ? '#FFD70022' : props.theme.background};
	color: ${props => props.claimed ? '#FFD700' : props.theme.text.secondary};
	border-radius: 8px;
	padding: 4px 8px;
	font-size: 12px;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const ActionButton = styled.button`
	background: ${props => props.theme.primary};
	color: white;
	border: none;
	border-radius: 12px;
	padding: 12px 24px;
	font-size: 16px;
	font-weight: 600;
	margin: 8px auto 0 auto;
	display: block;
	cursor: pointer;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.9;
	}

	&:active {
		opacity: 0.8;
	}
`;

const PulseButton = styled(ActionButton)`
	animation: pulse 1.5s infinite;
	@keyframes pulse {
		0% { box-shadow: 0 0 0 0 rgba(37,99,235,0.7); }
		70% { box-shadow: 0 0 0 10px rgba(37,99,235,0); }
		100% { box-shadow: 0 0 0 0 rgba(37,99,235,0); }
	}
`;

const TimerBlock = styled.div`
	text-align: center;
	color: ${props => props.theme.text.secondary};
	font-size: 14px;
	margin-top: 8px;
`;

export default DailyRewardModal; 